// @givmo/social — the module's reads, over the GENERATED operations (SMM-WEB1; §SMM-8 point 1:
// "data access = the generated api-client + TanStack Query").
//
// Two families: Social's own `/org/{org_ref}/social/*` (the composer and its history) and the
// shared store's `/org/{org_ref}/integrations*` with `purpose=social_publishing` (the connected
// accounts, the offered-providers read, a pending grant). One key root per organization, so one act
// refreshes every read it can change (`useRefreshSocial`).
//
// ⚠ A LIST'S ENVELOPE IS UNWRAPPED BEFORE IT ARRIVES (`api-client.md`, "The paginated-envelope
// type/runtime mismatch"): the body is the bare row array and `meta.total` is gone, so every list is
// read through `extractList` and the history pages by asking for one row more than it shows.
//
// The Collateral library's read is the one hand-written call: its family is not in the committed
// snapshot, so it goes through the `givmoClient` hatch, decoded by `@givmo/org-console` exactly as
// Fundraisers' cover picker reads it (`collateral-cover.tsx`). Taking an image from it is the
// generated `social-image` operation, which copies it server-side.

import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  extractList,
  givmoClient,
  isGivmoError,
  sdk,
  type OrgIntegrationConnectionOut,
  type OrgIntegrationOfferedProviderOut,
  type OrgIntegrationPendingGrantOut,
  type SocialPostOut,
} from '@givmo/api-client';
import { decodeCollateralList, orgCollateralKey, type CollateralItem } from '@givmo/org-console';
import { SOCIAL_PURPOSE } from './social-routes';

/** How many posts a history page shows. */
export const POSTS_PAGE_SIZE = 20;

/** A history read's filter: a variant status, and the Email Campaign context pair. */
export interface PostsFilter {
  status: string | null;
  contextKind: string | null;
  contextRef: string | null;
}

export function socialKeys(orgRef: string | null) {
  const root = ['social', orgRef ?? ''] as const;
  return {
    root,
    posts: (filter: PostsFilter, offset: number) => [...root, 'posts', filter, offset] as const,
    post: (postId: string) => [...root, 'post', postId] as const,
    connections: [...root, 'connections'] as const,
    offered: [...root, 'offered'] as const,
    pending: (grantId: string) => [...root, 'pending', grantId] as const,
  };
}

/** The route miss: the organization's Social (or the store's purpose) is not served here. */
export function isRouteMiss(error: unknown): boolean {
  return isGivmoError(error) && error.status === 404;
}

/** Never retry an answer: a 4xx is the server's word, not a blip. */
function retryOnlyBlips(failureCount: number, error: unknown): boolean {
  if (isGivmoError(error) && error.status !== undefined && error.status < 500) return false;
  return failureCount < 1;
}

/** One page of the history, newest first, and whether another page follows. */
export interface PostsPage {
  posts: SocialPostOut[];
  hasMore: boolean;
}

export function usePosts(orgRef: string | null, filter: PostsFilter, offset: number) {
  return useQuery({
    queryKey: socialKeys(orgRef).posts(filter, offset),
    enabled: orgRef !== null,
    retry: retryOnlyBlips,
    queryFn: async ({ signal }): Promise<PostsPage> => {
      const { data } = await sdk.orgSocialListOrgSocialPosts({
        path: { org_ref: orgRef ?? '' },
        query: {
          'page[limit]': POSTS_PAGE_SIZE + 1,
          'page[offset]': offset,
          ...(filter.status ? { status: filter.status } : {}),
          ...(filter.contextKind && filter.contextRef
            ? { context_kind: filter.contextKind, context_ref: filter.contextRef }
            : {}),
        },
        signal,
        throwOnError: true,
      });
      const rows = extractList<SocialPostOut>(data as unknown, 'data');
      return { posts: rows.slice(0, POSTS_PAGE_SIZE), hasMore: rows.length > POSTS_PAGE_SIZE };
    },
  });
}

export function usePost(orgRef: string | null, postId: string | null) {
  return useQuery({
    queryKey: socialKeys(orgRef).post(postId ?? ''),
    enabled: orgRef !== null && postId !== null,
    retry: retryOnlyBlips,
    queryFn: async ({ signal }) => {
      const { data } = await sdk.orgSocialGetOrgSocialPost({
        path: { org_ref: orgRef ?? '', post_id: postId ?? '' },
        signal,
        throwOnError: true,
      });
      return data;
    },
  });
}

/** How many posts read at most for a count the page states (the envelope's total never arrives:
 *  a count past it is said as "more than"). */
export const COUNT_LIMIT = 100;

/** A count of the posts one read finds, and whether the read stopped at its limit. */
export interface PostsCount {
  count: number;
  capped: boolean;
}

/** The posts that need a person's attention: any account's post that didn't go, or couldn't be
 *  confirmed (both `failed` on the server). */
export function useAttentionCount(orgRef: string | null) {
  return useQuery({
    queryKey: [...socialKeys(orgRef).root, 'attention'] as const,
    enabled: orgRef !== null,
    retry: retryOnlyBlips,
    queryFn: async ({ signal }): Promise<PostsCount> => {
      const { data } = await sdk.orgSocialListOrgSocialPosts({
        path: { org_ref: orgRef ?? '' },
        query: { 'page[limit]': COUNT_LIMIT, 'page[offset]': 0, status: 'failed' },
        signal,
        throwOnError: true,
      });
      const rows = extractList<SocialPostOut>(data as unknown, 'data');
      return { count: rows.length, capped: rows.length >= COUNT_LIMIT };
    },
  });
}

/** How many of one account's posts are still scheduled (for the disconnect's warning). */
export function useScheduledCount(orgRef: string | null, connectionId: string | null) {
  return useQuery({
    queryKey: [...socialKeys(orgRef).root, 'scheduled-to', connectionId ?? ''] as const,
    enabled: orgRef !== null && connectionId !== null,
    retry: retryOnlyBlips,
    queryFn: async ({ signal }): Promise<PostsCount> => {
      const { data } = await sdk.orgSocialListOrgSocialPosts({
        path: { org_ref: orgRef ?? '' },
        query: { 'page[limit]': COUNT_LIMIT, 'page[offset]': 0, status: 'queued' },
        signal,
        throwOnError: true,
      });
      const rows = extractList<SocialPostOut>(data as unknown, 'data');
      const count = rows
        .flatMap((post) => post.variants ?? [])
        .filter(
          (variant) => variant.connection_id === connectionId && variant.status === 'queued',
        ).length;
      return { count, capped: rows.length >= COUNT_LIMIT };
    },
  });
}

/** The organization's Social accounts, disconnected ones left out. */
export function useConnections(orgRef: string | null) {
  return useQuery({
    queryKey: socialKeys(orgRef).connections,
    enabled: orgRef !== null,
    retry: retryOnlyBlips,
    queryFn: async ({ signal }): Promise<OrgIntegrationConnectionOut[]> => {
      const { data } = await sdk.orgIntegrationsListOrgIntegrations({
        path: { org_ref: orgRef ?? '' },
        query: { purpose: SOCIAL_PURPOSE, 'page[limit]': 100 },
        signal,
        throwOnError: true,
      });
      return extractList<OrgIntegrationConnectionOut>(data as unknown, 'data').filter(
        (row) => row.status !== 'disconnected',
      );
    },
  });
}

/** One Connect per grant family the store offers here, each with its offered providers. */
export interface OfferedFamily {
  family: string;
  providers: string[];
}

export function groupFamilies(rows: readonly OrgIntegrationOfferedProviderOut[]): OfferedFamily[] {
  const families: OfferedFamily[] = [];
  for (const row of rows) {
    if (typeof row.grant_family !== 'string' || typeof row.provider !== 'string') continue;
    const known = families.find((entry) => entry.family === row.grant_family);
    if (known) {
      if (!known.providers.includes(row.provider)) known.providers.push(row.provider);
    } else {
      families.push({ family: row.grant_family, providers: [row.provider] });
    }
  }
  return families;
}

export function useOfferedFamilies(orgRef: string | null) {
  return useQuery({
    queryKey: socialKeys(orgRef).offered,
    enabled: orgRef !== null,
    retry: retryOnlyBlips,
    queryFn: async ({ signal }): Promise<OfferedFamily[]> => {
      const { data } = await sdk.orgIntegrationsListOrgIntegrationOfferedProviders({
        path: { org_ref: orgRef ?? '' },
        query: { purpose: SOCIAL_PURPOSE, 'page[limit]': 100 },
        signal,
        throwOnError: true,
      });
      return groupFamilies(extractList<OrgIntegrationOfferedProviderOut>(data as unknown, 'data'));
    },
  });
}

export function usePendingGrant(orgRef: string | null, grantId: string | null) {
  return useQuery({
    queryKey: socialKeys(orgRef).pending(grantId ?? ''),
    enabled: orgRef !== null && grantId !== null,
    retry: false,
    queryFn: async ({ signal }): Promise<OrgIntegrationPendingGrantOut> => {
      const { data } = await sdk.orgIntegrationsGetOrgIntegrationPending({
        path: { org_ref: orgRef ?? '', grant_id: grantId ?? '' },
        signal,
        throwOnError: true,
      });
      return data;
    },
  });
}

/**
 * The Collateral library's images IN USE, newest first, for the composer's campaign entry: an
 * item marked out of date is left out of this list, as the assistant leaves it out (the
 * library's owner, D-EMC-21); the Collateral page itself is where one is put back in use. Nothing
 * where the library does not answer (dark, or a person who doesn't read it): never an error here.
 */
export function useCollateralImages(orgRef: string | null, enabled: boolean) {
  return useQuery({
    queryKey: [...orgCollateralKey(orgRef ?? ''), 'social-images'] as const,
    enabled: enabled && orgRef !== null,
    retry: false,
    queryFn: async ({ signal }): Promise<CollateralItem[]> => {
      try {
        const { data } = await givmoClient.get({
          url: `/org/${encodeURIComponent(orgRef ?? '')}/profile/collateral`,
          query: { kind: 'image', status: 'current', limit: 100, offset: 0 },
          signal,
          throwOnError: true,
        });
        return decodeCollateralList(data).items.filter(
          (item) => item.kind === 'image' && !item.outOfDate,
        );
      } catch {
        return [];
      }
    },
  });
}

/** Refresh every Social read of one organization: one act can change the history, a post and
 *  the accounts at once. */
export function useRefreshSocial(orgRef: string | null) {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: socialKeys(orgRef).root });
}
